import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { useTranslation } from '@lib/useTypedTranslation';

import { DeviceInfoContext } from '../../../../device';
import { List } from '../../../../../../components/list/list';
import { InitialTableState, TableName, useTableReducer } from '../../../../../../components/data-table/lib';
import { ITableColumn } from '../../../../../../components/data-table/types';
import { ITranslationKeys } from '../../../../../../components/i18n/keys';
import { useWorldRequest } from '../../../../../../lib/useWorldRequest';
import { RequestInitWithRetry } from '../../../../../../lib/request';
import { useWorldSettingsContext } from '../../../../../../context/worldSettings';
import { useUserSettingsContext } from '../../../../../../context/userSettings';
import { ChipButton } from '../../../../../../components/controls/chipButton';
import {
  getSelectedTzDateDeviceEventsList,
  getSelectedTzDateDeviceEventsCount,
  IProcessEventsSummaryItem,
  IProcessEventsSummaryData,
  IProcessEventsSummaryParams
} from '../../../../../../services/core/devicePerformance';
import { ProcessEventName, TzDeviceEvent } from '../../../../../../services/core/eventsTypes';
import { AnomalySectionProps } from './index';
import { useProcessEventsRawColumns } from './useProcessEventsRawColumns';
import { useProcessEventsSummaryColumns } from './useProcessEventsSummaryColumns';

import './processEventsSection.css';

const defaultPageSize = 20;

type ProcessEventsView = 'raw' | 'summary';

// Rendered via both Lists' own additionalHeaderItems (see rawTable/summaryTable below), so it
// appears on the right of the List's built-in header row - alongside its title on the left -
// exactly where AnomalyTypePicker sits in deviceAnomaliesList.tsx's own List header, rather than
// as a separate row above the table.
function ProcessEventsViewToggle(props: { currentView: ProcessEventsView, setCurrentView: (view: ProcessEventsView) => void, t: (key: string) => string }) {
  const { currentView, setCurrentView, t } = props;
  return (
    <div className="device-anomaly-examination-page__process-events-toggle">
      <ChipButton text={t('RAW_DATA')} active={currentView === 'raw'} onClick={() => setCurrentView('raw')} />
      <ChipButton text={t('SUMMARY')} active={currentView === 'summary'} onClick={() => setCurrentView('summary')} />
    </div>
  );
}

export interface ProcessEventsSectionProps extends AnomalySectionProps {
  /** The single process event name this section's tables query and group by. */
  eventName: ProcessEventName,
  /** Distinct table names for this section's own Raw Data / Summary tables, so page size
   * persistence (see useTableReducer/tableSettings.ts) doesn't collide between the process event
   * types sharing this component. */
  rawTableName: TableName,
  summaryTableName: TableName,
  /** The type-specific summary fetcher (getDeviceProcessHangsSummary, getDeviceProcessCrashesSummary). */
  getSummary: (params: IProcessEventsSummaryParams) => (options: RequestInitWithRetry) => Promise<IProcessEventsSummaryData>,
  /** Translation key for both tables' shared title range, e.g. PROCESS_HANGS_FOR_RANGE. */
  titleKey: keyof ITranslationKeys['anomalyDetectionBeta'],
  /** Translation key for the Raw Data table's pluralized status text, e.g. PROCESS_HANG_RESULT. */
  rawStatusTextKey: keyof ITranslationKeys['anomalyDetectionBeta'],
  /** Translation key for the Summary table's Number of <Events> column, e.g. NUMBER_OF_HANGS. */
  countColumnTitleKey: keyof ITranslationKeys['anomalyDetectionBeta'],
  /** Columns specific to this event type, appended after the shared process/product/version
   * columns every Raw Data table has (see useProcessEventsRawColumns) - e.g. Reason for process
   * hangs, Exception Code/Module for process crashes. */
  eventColumns: ITableColumn[],
  /** Base data-id for this section - used directly for the wrapping <div>, and suffixed -raw /
   * -summary for each List. */
  dataId: string
}

/**
 * The examination page's shared section for any single process event type: a Raw Data / Summary
 * toggle over the selected day's events of that type for this device. Unlike every chart-based
 * sibling section (lowPowerSection, eventTimelineSection), this renders no chart - see index.ts's
 * own chartCount: 0 registration for every process event type, and AnomalySectionProps.onChartMount's
 * own comment for why it's fine to ignore that prop here.
 *
 * Extracted from what was originally ProcessHangsSection's own body once ProcessCrashesSection
 * needed the identical shape - see those two thin, type-specific wrappers for their own config.
 *
 * Both views are ordinary server-side paged/sorted List tables (see accessPointsList.tsx,
 * estate-overview.tsx) - grouping for the Summary view happens in the middleware
 * (deviceProcessEventsSummary/common.ts), not in this component, matching how every other table
 * in the app works.
 */
export function ProcessEventsSection(props: ProcessEventsSectionProps) {
  const { deviceId, selectedDate, eventName, rawTableName, summaryTableName, getSummary, titleKey, rawStatusTextKey, countColumnTitleKey, eventColumns, dataId } = props;
  const { t } = useTranslation('anomalyDetectionBeta');
  const { timeZone } = useContext(DeviceInfoContext);
  const { worldSettings } = useWorldSettingsContext();
  const { tablePageSizes } = useUserSettingsContext();

  const [currentView, setCurrentView] = useState<ProcessEventsView>('raw');

  // World-level anomaly-detection time zone selectedDate was bucketed by (see
  // deviceAnomalyEventChart.ts/fillingInDataForDays.ts) - defaults to 'UTC' to match
  // deviceAnomalyExaminationPage.tsx's own fallback for the top chart.
  const anomalyTz = worldSettings.smartGroupingTimeZone || 'UTC';

  const selectedDateUtc = useMemo(() => {
    const selectedDay = moment.utc(selectedDate).format('YYYY-MM-DD');
    return {
      from: moment.tz(selectedDay, anomalyTz).startOf('day').valueOf(),
      to: moment.tz(selectedDay, anomalyTz).endOf('day').valueOf()
    };
  }, [selectedDate, anomalyTz]);

  const deviceTimeZone = timeZone?.name || 'UTC';

  const title = t(titleKey, {
    from: moment.utc(selectedDateUtc.from).tz(deviceTimeZone).format('Do MMMM HH:mm'),
    to: moment.utc(selectedDateUtc.to).tz(deviceTimeZone).format('Do MMMM HH:mm'),
    timeZone: timeZone?.name || 'UTC'
  });

  const rawInitialTableState: InitialTableState = {
    limit: tablePageSizes?.[rawTableName] || defaultPageSize,
    sort: { column: 0, direction: 'desc', field: 'utc' }
  };
  const [rawTableReducerProperties, rawTableReducerFunctions] = useTableReducer<TzDeviceEvent>(rawTableName, rawInitialTableState);
  const { offset: rawOffset, limit: rawLimit, sort: rawSort, dataRequestId: rawDataRequestId } = rawTableReducerProperties;
  const { onLoading: onRawLoading, onDataLoaded: onRawDataLoaded } = rawTableReducerFunctions;

  const summaryInitialTableState: InitialTableState = {
    limit: tablePageSizes?.[summaryTableName] || defaultPageSize,
    sort: { column: 4, direction: 'desc', field: 'count' }
  };
  const [summaryTableReducerProperties, summaryTableReducerFunctions] = useTableReducer<IProcessEventsSummaryItem>(summaryTableName, summaryInitialTableState);
  const { offset: summaryOffset, limit: summaryLimit, sort: summarySort } = summaryTableReducerProperties;
  const { onLoading: onSummaryLoading } = summaryTableReducerFunctions;

  // Both views examine the same day - re-load both reducers whenever the examined day changes
  // (via the top chart's own onDayClick, which re-syncs selectedDate on this already-mounted
  // section rather than navigating again), so a stale previous day's data/loading state never
  // lingers behind while the new day's fetch is in flight. Mirrors performance-events.tsx's own
  // onLoading-on-range-change guard.
  useEffect(() => {
    onRawLoading();
    onSummaryLoading();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateUtc.to]);

  const rawCountFetcher = useCallback(() => {
    return getSelectedTzDateDeviceEventsCount({ deviceId, to: selectedDateUtc.to, tz: anomalyTz, events: [eventName] });
  }, [deviceId, selectedDateUtc.to, anomalyTz, eventName]);
  const { data: rawTotal } = useWorldRequest(rawCountFetcher, { initialLoading: true, initialData: { count: 0 } });

  const rawListFetcher = useCallback(() => {
    return getSelectedTzDateDeviceEventsList({
      deviceId,
      to: selectedDateUtc.to,
      tz: anomalyTz,
      events: [eventName],
      limit: rawLimit,
      offset: rawOffset,
      sort: { field: rawSort.field, order: rawSort.direction }
    });
  }, [deviceId, selectedDateUtc.to, anomalyTz, eventName, rawLimit, rawOffset, rawSort.field, rawSort.direction]);

  const handleRawListFetched = useCallback((events: TzDeviceEvent[]) => {
    onRawDataLoaded(events, rawTotal.count, rawDataRequestId);
  }, [onRawDataLoaded, rawTotal.count, rawDataRequestId]);

  const summaryListFetcher = useCallback(() => {
    return getSummary({
      deviceId,
      to: selectedDateUtc.to,
      tz: anomalyTz,
      limit: summaryLimit,
      offset: summaryOffset,
      sort: { field: summarySort.field, order: summarySort.direction }
    });
  }, [getSummary, deviceId, selectedDateUtc.to, anomalyTz, summaryLimit, summaryOffset, summarySort.field, summarySort.direction]);

  const rawColumns = useProcessEventsRawColumns(deviceTimeZone, eventColumns);
  const summaryColumns = useProcessEventsSummaryColumns(countColumnTitleKey);

  const viewToggle = [{
    Component: ProcessEventsViewToggle,
    props: { currentView, setCurrentView, t }
  }];

  const rawTable = (
    <List<TzDeviceEvent, TzDeviceEvent[], void, void, 'id'>
      dataId={`${dataId}-raw`}
      customHeader={false}
      hasCheckboxes={false}
      hasFilters={false}
      hasSearch={false}
      translationItems={{
        statusText: { key: rawStatusTextKey, ns: 'anomalyDetectionBeta' },
        tableTitle: { text: title }
      }}
      columns={rawColumns}
      fetcher={rawListFetcher}
      onListFetched={handleRawListFetched}
      tableReducerFunctions={rawTableReducerFunctions}
      tableReducerProperties={rawTableReducerProperties}
      useRequestHook={useWorldRequest}
      total={rawTotal.count}
      options={{ rowIdDataPath: 'id' }}
      additionalHeaderItems={viewToggle}
    />
  );

  const summaryTable = (
    <List<IProcessEventsSummaryItem, IProcessEventsSummaryData, 'totalCount', 'list'>
      dataId={`${dataId}-summary`}
      customHeader={false}
      hasCheckboxes={false}
      hasFilters={false}
      hasSearch={false}
      countDataPath="totalCount"
      listDataPath="list"
      translationItems={{
        statusText: { key: 'RESULT', ns: 'translation' },
        tableTitle: { text: title }
      }}
      columns={summaryColumns}
      fetcher={summaryListFetcher}
      tableReducerFunctions={summaryTableReducerFunctions}
      tableReducerProperties={summaryTableReducerProperties}
      useRequestHook={useWorldRequest}
      options={{ getKey: (item) => JSON.stringify([item.processName, item.fileVersion, item.productName, item.productVersion]) }}
      additionalHeaderItems={viewToggle}
    />
  );

  return (
    <div data-id={dataId}>
      {currentView === 'raw' ? rawTable : summaryTable}
    </div>
  );
}
